/**
 * TradeStack - Expenses Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Expenses.css';

function Expenses() {
  const { config, formatCurrency, formatDate } = useConfig();
  const [searchParams, setSearchParams] = useSearchParams();

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allExpenses, setAllExpenses] = useState([]); // For calculating totals

  const categoryFilter = searchParams.get('category') || '';
  const page = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    loadExpenses();
    loadAllExpensesForTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, page]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.getExpenses({
        category: categoryFilter,
        limit: 20,
        offset: (page - 1) * 20
      });
      setExpenses(response.data.expenses || []);
    } catch (err) {
      setError('Failed to load expenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load all expenses (no pagination) to calculate YTD totals
  const loadAllExpensesForTotals = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await api.getExpenses({
        startDate: `${currentYear}-01-01`,
        limit: 10000 // Get all expenses for the year
      });
      console.log('All expenses for totals:', response.data.expenses);
      setAllExpenses(response.data.expenses || []);
    } catch (err) {
      console.error('Failed to load expenses for totals:', err);
    }
  };

  // Calculate totals from all expenses
  const yearToDateTotal = allExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  const taxDeductibleTotal = allExpenses
    .filter(exp => exp.is_tax_deductible)
    .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

  const handleCategoryFilter = (category) => {
    const params = new URLSearchParams(searchParams);
    if (category) {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const categories = config?.expenseCategories || [
    { id: 'materials', name: 'Materials' },
    { id: 'tools', name: 'Tools & Equipment' },
    { id: 'fuel', name: 'Fuel' },
    { id: 'subcontractor', name: 'Subcontractor' },
    { id: 'advertising', name: 'Advertising' },
    { id: 'meals', name: 'Meals' },
    { id: 'office', name: 'Office Supplies' },
    { id: 'insurance', name: 'Insurance' },
    { id: 'other', name: 'Other' }
  ];

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  return (
    <div className="expenses-page">
      <div className="page-header">
        <h1>Expenses</h1>
        <div className="header-actions">
          <Link to="/receipts/scan" className="btn btn-secondary">Scan Receipt</Link>
          <Link to="/expenses/new" className="btn btn-primary">+ Add Expense</Link>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="expense-summary">
        <div className="summary-card">
          <span className="summary-label">Year to Date</span>
          <span className="summary-value">{formatCurrency(yearToDateTotal)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Tax Deductible</span>
          <span className="summary-value positive">{formatCurrency(taxDeductibleTotal)}</span>
        </div>
      </div>

      {/* Category Filter */}
      <div className="filters-bar">
        <div className="status-filters">
          <button
            className={`filter-btn ${!categoryFilter ? 'active' : ''}`}
            onClick={() => handleCategoryFilter('')}
          >
            All
          </button>
          {categories.slice(0, 6).map(category => (
            <button
              key={category.id}
              className={`filter-btn ${categoryFilter === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryFilter(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Expenses List */}
      {loading ? (
        <div className="loading-state">Loading expenses...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : expenses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">💳</div>
          <h3>No expenses found</h3>
          <p>Start tracking your business expenses</p>
          <Link to="/expenses/new" className="btn btn-primary">Add Expense</Link>
        </div>
      ) : (
        <div className="expenses-list">
          {expenses.map(expense => (
            <Link to={`/expenses/${expense.id}`} key={expense.id} className="expense-card">
              <div className="expense-icon">
                {expense.category === 'fuel' ? '⛽' :
                 expense.category === 'materials' ? '🏗️' :
                 expense.category === 'tools' ? '🔧' :
                 expense.category === 'meals' ? '🍔' :
                 expense.category === 'subcontractor' ? '👷' :
                 expense.category === 'advertising' ? '📢' : '💵'}
              </div>
              <div className="expense-info">
                <span className="expense-vendor">{expense.vendor || 'Unknown Vendor'}</span>
                <span className="expense-category">{getCategoryName(expense.category)}</span>
                <span className="expense-date">{formatDate(expense.expense_date)}</span>
              </div>
              <div className="expense-details">
                {expense.job_title && (
                  <span className="expense-job">{expense.job_title}</span>
                )}
                {expense.description && (
                  <span className="expense-desc">{expense.description}</span>
                )}
              </div>
              <div className="expense-amount">
                <span className="amount">{formatCurrency(expense.amount)}</span>
                {expense.is_tax_deductible && (
                  <span className="tax-badge">Tax Deductible</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default Expenses;
